import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiX } from 'react-icons/fi';
import { searchMovies, fetchByGenre } from '../api/tmdb';
import MovieCard from '../components/MovieCard';
import GenreFilter from '../components/GenreFilter';
import Loader from '../components/Loader';
import Footer from '../components/Footer';

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialQ = params.get('q') || '';

  const [query, setQuery] = useState(initialQ);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genre, setGenre] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const timerRef = useRef();

  const doSearch = (q, g, p = 1) => {
    setLoading(true);
    const fn = q.trim()
      ? searchMovies(q, p)
      : g
      ? fetchByGenre(g)
      : searchMovies('popular', p);

    fn.then(res => {
      const data = res.data;
      setResults(p === 1 ? data.results : prev => [...prev, ...data.results]);
      setTotalPages(data.total_pages || 1);
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setPage(1);
      doSearch(query, genre, 1);
      if (query) navigate('/search?q=' + encodeURIComponent(query), { replace: true });
    }, 400);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, genre]);

  const handleGenre = (g) => {
    setGenre(g);
    setQuery('');
    navigate('/search', { replace: true });
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    doSearch(query, genre, next);
  };

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', paddingTop: 88 }}>
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        {/* Search bar */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="relative mb-8"
        >
          <FiSearch size={20} className="absolute left-5 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search movies, genres, actors..."
            className="w-full pl-14 pr-12 py-4 rounded-2xl text-base font-body outline-none transition-all"
            style={{
              background: 'rgba(17,17,24,0.9)',
              border: '1px solid rgba(229,9,20,0.25)',
              color: 'var(--text)',
              boxShadow: '0 0 30px rgba(229,9,20,0.05)',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(229,9,20,0.6)'}
            onBlur={e => e.target.style.borderColor = 'rgba(229,9,20,0.25)'}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-5 top-1/2 -translate-y-1/2 hover:text-white transition-colors"
              style={{ color: 'var(--muted)' }}
            >
              <FiX size={18} />
            </button>
          )}
        </motion.div>

        {/* Genre filter */}
        <div className="mb-8 -mx-6 md:-mx-12">
          <GenreFilter selected={genre} onSelect={handleGenre} />
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl tracking-wide" style={{ color: 'var(--text)' }}>
            {query ? `Results for "${query}"` : genre ? 'Browse by Genre' : 'Discover Movies'}
          </h2>
          {results.length > 0 && (
            <span className="text-sm font-accent" style={{ color: 'var(--muted)' }}>
              {results.length} found
            </span>
          )}
        </div>

        {/* Grid */}
        {loading && results.length === 0 ? (
          <Loader />
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <FiSearch size={48} style={{ color: 'var(--border)' }} />
            <p className="font-body" style={{ color: 'var(--muted)' }}>No results found</p>
          </div>
        ) : (
          <>
            <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))' }}>
              {results.map((movie, i) => (
                <motion.div
                  key={movie.id + '-' + i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 20) * 0.03 }}
                  className="flex justify-center"
                >
                  <MovieCard movie={movie} />
                </motion.div>
              ))}
            </div>

            {page < totalPages && (
              <div className="flex justify-center mt-10">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={loadMore}
                  disabled={loading}
                  className="px-10 py-3 rounded-full font-body font-semibold text-sm transition-all"
                  style={{
                    background: loading ? 'rgba(229,9,20,0.4)' : 'var(--red)',
                    color: '#fff',
                    boxShadow: '0 0 20px rgba(229,9,20,0.3)',
                  }}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SearchPage;
